import React from "react";
import { Box, Dropdown, Menu, MenuButton } from "@mui/joy";
import IconButtonCTA from "components/common/IconButtonCTA";
import { CURATED_EMOJI_OPTIONS } from "components/common/curatedEmojiOptions";
import { colorTokens } from "styles/globalStyles";

export type CuratedEmojiPickerProps = {
  onPick: (emoji: string) => void;
  options?: readonly string[];
  /** Highlighted option (selection mode). */
  selected?: string | null;
  disabled?: boolean;
  ariaLabel?: string;
  menuZIndex?: number;
  /** Button face; defaults to selected emoji or "😊". */
  triggerLabel?: React.ReactNode;
  triggerSizePx?: number;
};

export default function CuratedEmojiPicker({
  onPick,
  options = CURATED_EMOJI_OPTIONS,
  selected,
  disabled,
  ariaLabel = "Insérer un emoji",
  menuZIndex = 2200,
  triggerLabel,
  triggerSizePx = 32,
}: CuratedEmojiPickerProps) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLSpanElement | null>(null);
  const menuId = React.useId();

  const face =
    triggerLabel !== undefined ? triggerLabel : selected?.trim() ? selected.trim() : "😊";

  React.useEffect(() => {
    if (!open) return;

    const onPointerDown = (ev: PointerEvent) => {
      const target = ev.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target)) return;
      const menuEl = document.getElementById(menuId);
      if (menuEl?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown, true);

    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [menuId, open]);

  return (
    <span ref={rootRef} style={{ display: "inline-flex" }}>
      <Dropdown
        open={open}
        onOpenChange={(_, next) => {
          if (!disabled) setOpen(!!next);
        }}
      >
        <MenuButton
          slots={{ root: IconButtonCTA }}
          slotProps={{
            root: {
              size: "sm",
              variant: "plain",
              disabled,
              "aria-label": ariaLabel,
              ctaVariant: "accent",
              onMouseDown: (e: React.MouseEvent) => {
                e.preventDefault();
              },
              sx: {
                "--IconButton-size": `${triggerSizePx}px`,
                minWidth: triggerSizePx,
                minHeight: Math.max(triggerSizePx, 32),
                px: triggerSizePx < 28 ? 0.25 : undefined,
                fontSize: triggerSizePx < 28 ? "1rem" : "1.1rem",
                lineHeight: 1,
                color: `${colorTokens.text.primary} !important`,
              },
            },
          }}
        >
          {face}
        </MenuButton>
        <Menu
          id={menuId}
          size="sm"
          variant="soft"
          placement="bottom-start"
          sx={{
            zIndex: menuZIndex,
            p: 0.75,
            minWidth: 220,
            maxWidth: { xs: "min(320px, 95vw)", sm: 320 },
            maxHeight: { xs: "min(55dvh, 380px)", sm: 400 },
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: 0.25,
              pt: 0.5,
              mt: 0.25,
            }}
          >
            {options.map((emoji) => (
              <IconButtonCTA
                key={emoji}
                size="sm"
                variant={selected === emoji ? "soft" : "plain"}
                aria-label={emoji}
                ctaVariant="accent"
                onClick={() => {
                  onPick(emoji);
                  setOpen(false);
                }}
                sx={{
                  "--IconButton-size": "36px",
                  minWidth: 36,
                  minHeight: 36,
                  fontSize: "1.15rem",
                  borderRadius: "sm",
                }}
              >
                {emoji}
              </IconButtonCTA>
            ))}
          </Box>
        </Menu>
      </Dropdown>
    </span>
  );
}
